import React, { useState, useEffect } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, Typography, TextField, MenuItem, Checkbox, Chip, IconButton } from "@mui/material";
import * as Yup from "yup";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import { FiX, FiChevronDown } from "react-icons/fi";
import { Formik } from "formik";
import { useCreateUserRoleMutation, useUpdateUserRoleMutation } from "store/slices/apis/userRoleApiSlice";
import { useParams } from "react-router-dom";

const AddEditRole = (props) => {
    const dispatch = useDispatch();
    const { projectId } = useParams();
    const { open, handleClose, roleData } = props;
    const isEditMode = Boolean(roleData);

    const [createUserRole] = useCreateUserRoleMutation();
    const [updateUserRole] = useUpdateUserRoleMutation();

    const [formData, setFormData] = useState({
        name: roleData?.displayName || "",
    });

    useEffect(() => {
        if (roleData) {
            setFormData({
                name: roleData.displayName,
            });
        }
    }, [roleData]);

    const validationSchema = Yup.object().shape({
        name: Yup.string().required("Role Name field is required"),
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {

        let reqBody = {
            ...values,
            projectId: projectId,
        };

        try {
            let response;

            if (isEditMode) {
                response = await updateUserRole({
                    body: {...reqBody, id: roleData?.id}
                }).unwrap();
                openNotification("Role updated successfully!", 'success');

            } else {
                response = await createUserRole({ body: reqBody }).unwrap();
                openNotification("Role added successfully!", 'success')
            }

            resetForm();
            setSubmitting(false);
            handleClose();
        } catch (error) {
            openNotification(error?.data?.message || error?.error || "Something went wrong", "error");
            //handleClose();
        } finally {
            setSubmitting(false);
        }
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    const labelTextStyle = {
        fontWeight: 500,
        fontSize: "16px",
        lineHeight: "100%",
        color: "#5F5F5F",
        fontFamily: "Outfit, sans-serif",
        mb: 1,
        mt: 3,
    };

    return (
        <>
            {/* ---------- Popup Dialog ---------- */}
            <Dialog
                open={open}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: { borderTopLeftRadius: "5px", borderTopRightRadius: "5px", backgroundColor: "#FFFFFF", width: "100%", overflowX: "hidden" },
                }}
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== "backdropClick") {
                        handleClose();
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid #ddd",
                        backgroundColor: "#ECECEC",
                        pb: 2,
                        pt: 3,
                        width: "100%",
                    }}
                >
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "27px", lineHeight: "100%", color: "#140F36", fontFamily: "Outfit, sans-serif" }}>
                            {isEditMode ? "Edit Role" : "Add New Role"}
                        </Typography>
                        <Typography sx={{
                            pt: 2, fontWeight: 400, fontSize: "14px", lineHeight: "100%", color: "#433F5E", fontFamily: "Outfit, sans-serif",
                        }}>
                             {isEditMode ? "Update role details" : "Add role"}
                        </Typography>
                    </Box>

                </DialogTitle>
                <DialogContent sx={{ width: "100%", p: 4, overflowX: "hidden", boxSizing: "border-box" }}>
                    <Formik
                        initialValues={formData}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            isSubmitting,
                            setFieldValue,
                            setFieldTouched,
                            setTouched, }) => (
                            <form onSubmit={handleSubmit}>
                                <Box sx={{ mt: 3 }}>
                                    <Typography sx={labelTextStyle}>
                                        Role Name
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        name="name"
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Enter Role Name"
                                        InputProps={{
                                            sx: {
                                                height: "50px",
                                                borderRadius: "8px",
                                                backgroundColor: "#fff",

                                                "& input::placeholder": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                    opacity: 1,
                                                },

                                                "& input": {
                                                    fontWeight: 400,
                                                    fontSize: "16px",
                                                    lineHeight: "100%",
                                                    letterSpacing: "0.16px",
                                                    color: "#0A0A0C",
                                                    fontFamily: "Outfit, sans-serif",
                                                },
                                            },
                                        }}
                                    />
                                    {touched.name && errors.name && (
                                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5 }}>
                                            {errors.name}
                                        </Typography>
                                    )}

                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: { xs: "column", md: "row" },
                                            gap: { xs: 2, md: 1 },
                                            width: "100%",
                                            boxSizing: "border-box",
                                            overflowX: "hidden",
                                            mt: 4,
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <Button onClick={handleClose}
                                            sx={{
                                                textTransform: "none",
                                                color: "#F15744",
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                lineHeight: "100%",
                                                fontFamily: "Outfit, sans-serif",
                                            }}>
                                            Cancel
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            sx={{
                                                backgroundColor: "#00344C",
                                                borderRadius: "25px",
                                                textTransform: "none",
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                lineHeight: "100%",
                                                fontFamily: "Outfit, sans-serif",
                                                color: "#FFFFFF",
                                                px: "18px",
                                                py: "12px",
                                            }}
                                        >
                                            Submit
                                        </Button>
                                    </Box>
                                </Box>
                            </form>
                        )}
                    </Formik>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default AddEditRole;